import axios from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  User, 
  Department, 
  Campaign, 
  CampaignWithSchedule,
  Schedule, 
  ScheduledEmployee,
  TimeEntry, 
  ClockInRequest, 
  ClockOutRequest,
  DashboardStats,
  TimeReport,
  RestrictedDevice,
  CoverageReport,
  ActivitySummary,
  ClockStatus,
  ActivityLog,
  NetworkValidation,
  VacationBalanceSummary
} from '../types';
import { envConfig } from '../config/environment';
import { generateDeviceId } from '../utils/deviceId';
import { getDeviceAssertionHeader, requestNeedsDeviceAssertion } from '../utils/deviceAssertion';

// Use environment-aware API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || envConfig.apiUrl;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and device ID to requests
api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Log if token is missing for authenticated endpoints
    if (config.url && (config.url.includes('/ip-approval/approve') || config.url.includes('/ip-approval/reject'))) {
      console.warn('⚠️ No auth token found for IP approval request:', config.url);
    }
  }
  // Add device ID to all requests
  const deviceId = generateDeviceId();
  if (deviceId) {
    config.headers['X-Device-ID'] = deviceId;
  }
  // For login / attendance actions, attach a signed machine assertion when the local Agent can
  // provide one (machine-bound restricted devices). Absence is handled by backend policy.
  if (requestNeedsDeviceAssertion(config.url)) {
    try {
      const assertion = await getDeviceAssertionHeader();
      if (assertion) {
        config.headers['X-Device-Assertion'] = assertion;
      }
    } catch {
      // Never block the request if assertion retrieval fails; backend decides fail-closed/open.
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for IP approval endpoints - let the component handle the error
      const url = error.config?.url || '';
      if (url.includes('/ip-approval/approve') || url.includes('/ip-approval/reject')) {
        return Promise.reject(error);
      }
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data: LoginRequest): Promise<{ data: LoginResponse }> =>
    api.post('/auth/login', data),
  
  verify: (): Promise<{ data: { success: boolean; user: User } }> =>
    api.get('/auth/verify'),
  
  logout: (): Promise<{ data: { success: boolean; message: string } }> =>
    api.post('/auth/logout'),

  /** Public: shared-workstation login policy for this browser Device ID. */
  getLoginDevicePolicy: (deviceId: string): Promise<{
    data: { success: boolean; data: { sharedWorkstation: boolean } };
  }> =>
    api.get('/auth/login-device-policy', {
      params: { deviceId },
      headers: deviceId ? { 'X-Device-ID': deviceId } : undefined,
    }),
};

// Users API
export const usersAPI = {
  getAll: (): Promise<{ data: { success: boolean; data: User[] } }> =>
    api.get('/users'),
  
  getProfile: (): Promise<{ data: { success: boolean; data: User } }> =>
    api.get('/users/profile'),
  
  create: (data: Partial<User>): Promise<{ data: { success: boolean; data: User } }> =>
    api.post('/users', data),
  
  update: (id: string, data: Partial<User>): Promise<{ data: { success: boolean; data: User } }> =>
    api.put(`/users/${id}`, data),
  
  delete: (id: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/users/${id}`),
  
  resetPassword: (id: string, newPassword: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.post(`/users/${id}/reset-password`, { newPassword }),

  /** Same employment: restore TEKS login/access (isActive=true); hireDate unchanged. */
  reactivate: (id: string): Promise<{ data: { success: boolean; message: string; data: User; mode: 'reactivate' } }> =>
    api.post(`/users/${id}/reactivate`),

  /** New employment spell: isActive=true + hireDate + mustChangePassword (default true). */
  rehire: (
    id: string,
    data: { hireDate: string; mustChangePassword?: boolean }
  ): Promise<{ data: { success: boolean; message: string; data: User; mode: 'rehire' } }> =>
    api.post(`/users/${id}/rehire`, data),
};

// Departments API
export const departmentsAPI = {
  getAll: (): Promise<{ data: { success: boolean; data: Department[] } }> =>
    api.get('/departments'),
  
  create: (data: Partial<Department>): Promise<{ data: { success: boolean; data: Department } }> =>
    api.post('/departments', data),
  
  update: (id: string, data: Partial<Department>): Promise<{ data: { success: boolean; data: Department } }> =>
    api.put(`/departments/${id}`, data),
  
  delete: (id: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/departments/${id}`),
};

// Campaigns API
export const campaignsAPI = {
  getAll: (params?: {
    activeOnly?: boolean;
    includeWithActivityFrom?: string;
    includeWithActivityTo?: string;
  }): Promise<{ data: { success: boolean; data: Campaign[] } }> =>
    api.get('/campaigns', { params }),
  
  create: (data: Partial<Campaign>): Promise<{ data: { success: boolean; data: Campaign } }> =>
    api.post('/campaigns', data),
  
  update: (id: string, data: Partial<Campaign>): Promise<{ data: { success: boolean; data: Campaign } }> =>
    api.put(`/campaigns/${id}`, data),
  
  delete: (id: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/campaigns/${id}`),
};

// Schedules API
export const schedulesAPI = {
  getMySchedule: (): Promise<{ data: { success: boolean; data: Schedule[] } }> =>
    api.get('/schedules/my-schedule'),
  
  getMyTodaySchedule: (): Promise<{ data: { success: boolean; data: Schedule[] } }> =>
    api.get('/schedules/my-today'),
  
  getAll: (): Promise<{ data: { success: boolean; data: Schedule[] } }> =>
    api.get('/schedules'),
  
  getById: (id: string): Promise<{ data: { success: boolean; data: Schedule } }> =>
    api.get(`/schedules/${id}`),
  
  create: (data: Partial<Schedule>): Promise<{ data: { success: boolean; data: Schedule } }> =>
    api.post('/schedules', data),
  
  update: (id: string, data: Partial<Schedule>): Promise<{ data: { success: boolean; data: Schedule } }> =>
    api.put(`/schedules/${id}`, data),
  
  delete: (id: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/schedules/${id}`),

  // New campaign-first endpoints
  getCampaignEmployees: (campaignId: string, date: string, endDate?: string): Promise<{ data: { success: boolean; data: { campaign: Campaign & { departmentId?: string }; scheduledEmployees: ScheduledEmployee[]; departmentEmployees: User[]; otherEmployees: User[]; scheduledUserIds?: string[]; busyUserIds?: string[] } } }> =>
    api.get(`/schedules/campaign/${campaignId}/employees/${date}`, { params: endDate ? { endDate } : undefined }),

  getCampaignSchedules: (campaignId: string, startDate: string, endDate: string): Promise<{ data: { success: boolean; data: Schedule[] } }> =>
    api.get(`/schedules/campaign/${campaignId}/date-range/${startDate}/${endDate}`),

  getEmployeeShifts: (startDate: string, endDate: string, userIds: string[]): Promise<{ data: { success: boolean; data: Schedule[] } }> =>
    api.post('/schedules/employee-shifts', { startDate, endDate, userIds }),

  publishSchedule: (campaignId: string, payload: { startDate: string; endDate: string; viewMode: 'weekly' | 'monthly' }): Promise<{ data: { success: boolean; message: string } }> =>
    api.post(`/schedules/campaign/${campaignId}/publish`, payload),

  getPublishStatus: (campaignId: string, startDate: string, endDate: string, viewMode: 'weekly' | 'monthly'): Promise<{ data: { success: boolean; data: any } }> =>
    api.get(`/schedules/campaign/${campaignId}/publish-status/${startDate}/${endDate}/${viewMode}`),
  
  transferEmployee: (data: {
    userId: string;
    fromCampaignId?: string;
    toCampaignId: string;
    date: string;
    startTime: string;
    endTime: string;
  }): Promise<{ data: { success: boolean; data: Schedule; message: string } }> =>
    api.post('/schedules/transfer-employee', data),

  // Personal schedule endpoints
  getMyScheduleForDate: (date: string): Promise<{ data: { success: boolean; data: Schedule[] } }> =>
    api.get(`/schedules/my-schedule/${date}`),
  
  getMyScheduleRange: (startDate: string, endDate: string): Promise<{ data: { success: boolean; data: Schedule[] } }> =>
    api.get(`/schedules/my-schedule/range/${startDate}/${endDate}`),

  // Schedule copy/repeat endpoints
  getCampaignSchedulesRange: (campaignId: string, startDate: string, endDate: string): Promise<{ data: { success: boolean; data: Schedule[] } }> =>
    api.get(`/schedules/campaign/${campaignId}/date-range/${startDate}/${endDate}`),
  
  copySchedules: (data: {
    campaignId: string;
    fromDate: string;
    toDate: string;
    copyType: 'day' | 'week';
    userIds?: string[];
  }): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.post('/schedules/copy-schedules', data),
};

// Time Entries API
export const timeEntriesAPI = {
  clockIn: (data: ClockInRequest): Promise<{ data: { success: boolean; data: TimeEntry; message: string } }> =>
    api.post('/time-entries/clock-in', data),
  
  clockOut: (data: ClockOutRequest): Promise<{ data: { success: boolean; data: TimeEntry; message: string } }> =>
    api.post('/time-entries/clock-out', data),
  
  breakStart: (data: { notes?: string }): Promise<{ data: { success: boolean; data: TimeEntry; message: string } }> =>
    api.post('/time-entries/break-start', data),
  
  breakEnd: (data: { notes?: string }): Promise<{ data: { success: boolean; data: TimeEntry; message: string } }> =>
    api.post('/time-entries/break-end', data),
  
  lunchStart: (data: { notes?: string }): Promise<{ data: { success: boolean; data: TimeEntry; message: string } }> =>
    api.post('/time-entries/lunch-start', data),
  
  lunchEnd: (data: { notes?: string }): Promise<{ data: { success: boolean; data: TimeEntry; message: string } }> =>
    api.post('/time-entries/lunch-end', data),

  bathroomStart: (data: { notes?: string }): Promise<{ data: { success: boolean; data: TimeEntry; message: string } }> =>
    api.post('/time-entries/bathroom-start', data),

  bathroomEnd: (data: { notes?: string }): Promise<{ data: { success: boolean; data: TimeEntry; message: string } }> =>
    api.post('/time-entries/bathroom-end', data),
  
  getStatus: (): Promise<{ data: { success: boolean; data: ClockStatus } }> =>
    api.get('/time-entries/status'),
  
  getAvailableCampaigns: (): Promise<{ data: { success: boolean; data: CampaignWithSchedule[] } }> =>
    api.get('/time-entries/available-campaigns'),

  getCoverageBlock: (campaignId: string): Promise<{ data: { success: boolean; data: { isBlocked: boolean; message?: string; coveringUserName?: string; coverageStatus?: string } } }> =>
    api.get('/time-entries/coverage-block', { params: { campaignId } }),
  
  getAll: (): Promise<{ data: { success: boolean; data: TimeEntry[] } }> =>
    api.get('/time-entries'),
  
  create: (data: Partial<TimeEntry> & { adjustmentReason?: string; adjustmentComment?: string }): Promise<{ data: { success: boolean; data: TimeEntry } }> =>
    api.post('/time-entries', data),
  
  update: (id: string, data: Partial<TimeEntry> & { adjustmentReason?: string; adjustmentComment?: string }): Promise<{ data: { success: boolean; data: TimeEntry } }> =>
    api.put(`/time-entries/${id}`, data),
  
  delete: (id: string, data?: { adjustmentReason?: string; adjustmentComment?: string; notes?: string }): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/time-entries/${id}`, { data }),
  
  getReport: (params: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    campaignId?: string;
    entryType?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: { success: boolean; data: TimeEntry[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
    api.get('/time-entries/report', { params }),

  getMissingPunches: (queryString: string): Promise<{ data: { success: boolean; data: any[] } }> =>
    api.get(`/time-entries/missing-punches${queryString ? `?${queryString}` : ''}`),
  
  getDailySummary: (params: {
    startDate?: string;
    endDate?: string;
    departmentId?: string;
    campaignId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: { success: boolean; data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
    api.get('/time-entries/daily-summary', { params }),
  
  getCampaignPerformance: (params: {
    startDate?: string;
    endDate?: string;
    campaignId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: { success: boolean; data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
    api.get('/time-entries/campaign-performance', { params }),
  
      getEmployeeAttendance: (params: {
        startDate?: string;
        endDate?: string;
        userId?: string;
        departmentId?: string;
        campaignId?: string;
        page?: number;
        limit?: number;
      }): Promise<{ data: { success: boolean; data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
        api.get('/time-entries/employee-attendance', { params }),
      getEmployeeTimeSheet: (params: {
        startDate?: string;
        endDate?: string;
        userId?: string;
        departmentId?: string;
        campaignId?: string;
        page?: number;
        limit?: number;
      }): Promise<{ data: { success: boolean; data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
        api.get('/time-entries/employee-time-sheet', { params }),
      
      getLiveStatus: (): Promise<{ data: { success: boolean; data: any[] } }> =>
        api.get('/time-entries/live-status'),
      
      getAbsenceReport: (params: string): Promise<{ data: { success: boolean; data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
        api.get(`/time-entries/absence-report?${params}`),
      
      getTardiesReport: (params: {
        startDate?: string;
        endDate?: string;
        userId?: string;
        departmentId?: string;
        campaignId?: string;
        status?: string;
        page?: number;
        limit?: number;
      }): Promise<{ data: { success: boolean; data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
        api.get('/time-entries/tardies-report', { params }),
      getValidationReport: (params: {
        startDate?: string;
        endDate?: string;
        userId?: string;
        departmentId?: string;
        campaignId?: string;
        severity?: string;
        page?: number;
        limit?: number;
      }): Promise<{ data: { success: boolean; data: any[]; summary: any; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
        api.get('/time-entries/validation-report', { params }),
    };

// Absence API
export const absenceAPI = {
  getAll: (params?: string): Promise<{ data: { success: boolean; data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
    api.get(`/absences${params ? `?${params}` : ''}`),
  
  create: (data: any): Promise<{ data: { success: boolean; data: any } }> =>
    api.post('/absences', data),
  
  update: (id: string, data: any): Promise<{ data: { success: boolean; data: any } }> =>
    api.put(`/absences/${id}`, data),
  
  delete: (id: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/absences/${id}`),
};

// Time Absence Requests API
export const timeAbsenceRequestAPI = {
  create: (data: any): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.post('/time-absence-requests', data),
  
  getMyRequests: (params?: any): Promise<{ data: { success: boolean; data: any[]; pagination: any } }> =>
    api.get('/time-absence-requests/my-requests', { params }),
  
  getPending: (params?: any): Promise<{ data: { success: boolean; data: any[]; pagination: any } }> =>
    api.get('/time-absence-requests/pending', { params }),
  
  getApproved: (params?: any): Promise<{ data: { success: boolean; data: any[]; pagination: any } }> =>
    api.get('/time-absence-requests/approved', { params }),
  
  approve: (id: string, data: any): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put(`/time-absence-requests/${id}/approve`, data),
  
  reject: (id: string, data: any): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put(`/time-absence-requests/${id}/reject`, data),
  
  cancel: (id: string): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put(`/time-absence-requests/${id}/cancel`),
  
  cancelByApprover: (id: string, reason?: string): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put(`/time-absence-requests/${id}/cancel-by-approver`, { reason }),
  
  update: (id: string, data: any): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put(`/time-absence-requests/${id}`, data),
  
  delete: (id: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/time-absence-requests/${id}`),
  
  getStats: (params?: any): Promise<{ data: { success: boolean; data: any } }> =>
    api.get('/time-absence-requests/stats', { params }),

  getReport: (params?: Record<string, string | number>): Promise<{ data: { success: boolean; data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } }> =>
    api.get('/time-absence-requests/report', { params }),
  
  sendReminder: (id: string): Promise<{ data: { success: boolean; message: string; reminderCount: number } }> =>
    api.post(`/time-absence-requests/${id}/send-reminder`)
};

// Dashboard API
export const dashboardAPI = {
  getStats: (): Promise<{ data: { success: boolean; data: DashboardStats } }> =>
    api.get('/dashboard/stats'),
  
  getAnalytics: (): Promise<{ data: { success: boolean; data: any } }> =>
    api.get('/dashboard/analytics'),

  getRecentActivity: (): Promise<{ data: { success: boolean; data: any[] } }> =>
    api.get('/dashboard/recent-activity'),
  
  getMissingAgents: (): Promise<{ data: { success: boolean; data: any[] } }> =>
    api.get('/dashboard/missing-agents'),

  recordMissingAgentAbsence: (data: {
    userId: string;
    absenceReason: string;
    notes?: string;
    workDate?: string;
  }): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.post('/dashboard/missing-agents/record-absence', data),

  createMissingAgentsShare: (data: { agents: any[]; workDate: string }): Promise<{ data: { success: boolean; data: any } }> =>
    api.post('/dashboard/missing-agents/share', data),

  getMissingAgentsShare: (token: string): Promise<{ data: { success: boolean; data: any } }> =>
    api.get(`/dashboard/missing-agents/share/${token}`),

  previewMissingAgentsShare: (data: { agents: any[]; workDate: string }): Promise<{ data: { success: boolean; data: any } }> =>
    api.post('/dashboard/missing-agents/share/preview', data),

  emailMissingAgentsShare: (data: {
    agents: any[];
    workDate: string;
    recipients: string[];
    subject?: string;
    message?: string;
  }): Promise<{ data: { success: boolean; message: string; data?: any } }> =>
    api.post('/dashboard/missing-agents/share/email', data),

  smsMissingAgentsShare: (data: {
    agents: any[];
    workDate: string;
    smsRecipients: string[];
    message?: string;
  }): Promise<{ data: { success: boolean; message: string; data?: any } }> =>
    api.post('/dashboard/missing-agents/share/sms', data),
};

// Reports API
export const reportsAPI = {
  getTimeReports: (filters?: any): Promise<{ data: { success: boolean; data: TimeReport[] } }> =>
    api.get('/reports/time', { params: filters }),
  
  getCoverageReports: (filters?: any): Promise<{ data: { success: boolean; data: CoverageReport[] } }> =>
    api.get('/reports/coverage', { params: filters }),
  
  getScheduledEmployees: (filters: { date: string; department?: string; status?: string; campaign?: string; employee?: string }): Promise<{ data: { success: boolean; data: any[]; total: number; date: string; timezone: string } }> =>
    api.get('/reports/scheduled-employees', { params: filters }),

  getWeeklyStaffingCoverage: (filters?: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
    api.get('/reports/weekly-staffing-coverage', { params: filters }),
};

// Activity API
export const activityAPI = {
  logActivity: (data: { sessionId: string; activityType: string; metadata?: any }): Promise<{ data: { success: boolean; data: ActivityLog } }> =>
    api.post('/activity/log', data),
  
  getSummary: (sessionId: string, startTime: string, endTime: string): Promise<{ data: { success: boolean; data: ActivitySummary } }> =>
    api.get('/activity/summary', { params: { sessionId, startTime, endTime } }),
  
  getDailyReport: (date?: string): Promise<{ data: { success: boolean; data: any[] } }> =>
    api.get('/activity/daily', { params: { date } }),
  
  getAuditLog: (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    actionType?: string;
    executedBy?: string;
    search?: string;
  }): Promise<{ data: { success: boolean; data: { logs: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } } } }> =>
    api.get('/activity/audit-log', { params }),
};

// Roles API
export const rolesAPI = {
  getAll: (): Promise<{ data: { success: boolean; data: any[] } }> =>
    api.get('/roles'),
  
  getById: (id: string): Promise<{ data: { success: boolean; data: any } }> =>
    api.get(`/roles/${id}`),
  
  create: (data: any): Promise<{ data: { success: boolean; data: any } }> =>
    api.post('/roles', data),
  
  update: (id: string, data: any): Promise<{ data: { success: boolean; data: any } }> =>
    api.put(`/roles/${id}`, data),
  
  delete: (id: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/roles/${id}`),
};

// Password API
export const passwordAPI = {
  forgotPassword: (email: string, extras?: { source?: string; deviceId?: string }): Promise<{
    data: {
      success: boolean;
      message: string;
      data?: {
        requestId?: string | null;
        maskedEmail?: string | null;
        cooldownSeconds?: number;
        /** Present only when PASSWORD_RECOVERY_QA_RETURN_OTP=true in non-production. */
        qaOtp?: string;
      };
    };
  }> => api.post('/password/forgot-password', { email, ...extras }),

  verifyEmailOtp: (requestId: string, code: string): Promise<{
    data: { success: boolean; data?: { recoveryToken: string; nextStep: string } };
  }> => api.post('/password/recovery/email-otp/verify', { requestId, code }),

  requestSupervisorAssistance: (email: string, extras?: { source?: string; deviceId?: string }): Promise<{
    data: { success: boolean; message: string; data?: { requestId?: string | null } };
  }> => api.post('/password/recovery/supervisor/request', { email, ...extras }),

  verifySupervisorCode: (requestId: string, code: string): Promise<{
    data: { success: boolean; data?: { recoveryToken: string; nextStep: string } };
  }> => api.post('/password/recovery/supervisor/verify', { requestId, code }),

  setRecoveredPassword: (recoveryToken: string, newPassword: string, confirmPassword: string): Promise<{
    data: { success: boolean; message: string; token?: string; user?: any };
  }> => api.post('/password/recovery/set-password', { recoveryToken, newPassword, confirmPassword }),

  listSupervisorRequests: (): Promise<{ data: { success: boolean; data: any[] } }> =>
    api.get('/password/recovery/supervisor/requests'),

  generateSupervisorCode: (requestId: string): Promise<{
    data: { success: boolean; message: string; data?: { code: string; expiresAt: string; expiresInMinutes: number } };
  }> => api.post(`/password/recovery/supervisor/requests/${requestId}/generate-code`, {}),

  changePassword: (currentPassword: string, newPassword: string, confirmPassword?: string): Promise<{
    data: { success: boolean; message: string; token?: string };
  }> => api.post('/password/change-password', { currentPassword, newPassword, confirmPassword }),

  forceChangePassword: (newPassword: string, confirmPassword: string, currentPassword?: string): Promise<{
    data: { success: boolean; message: string; token?: string };
  }> => api.post('/password/force-change-password', { newPassword, confirmPassword, currentPassword }),
};

// Unified API export
// Network Configuration API
export const networkConfigAPI = {
  getConfig: (): Promise<{ data: { success: boolean; data: { companyNetworks: string[]; vpnNetworks: string[]; companyHostnames?: string[]; resolvedHostnameCidrs?: string[] } } }> =>
    api.get('/network-config/config'),

  updateConfig: (config: { companyNetworks: string[]; vpnNetworks: string[]; companyHostnames?: string[] }): Promise<{ data: { success: boolean; message: string } }> =>
    api.put('/network-config/config', config),
  
  testValidation: (ip: string, workLocation: 'on_site' | 'remote' | 'hybrid'): Promise<{ data: { success: boolean; data: NetworkValidation } }> =>
    api.post('/network-config/test', { ip, workLocation }),
  
  getMyStatus: (): Promise<{ data: { success: boolean; data: { userIP: string; workLocation: string; validation: NetworkValidation } } }> =>
    api.get('/network-config/my-status'),
};

// Restricted Devices API
export const restrictedDevicesAPI = {
  getAll: (): Promise<{ data: { success: boolean; data: RestrictedDevice[] } }> =>
    api.get('/restricted-devices'),
  
  create: (device: {
    deviceId: string;
    deviceName?: string | null;
    allowedCampaignIds: string[];
    allowedRoleIds: string[];
    sharedWorkstation?: boolean;
    machineDeviceId?: string;
  }): Promise<{ data: { success: boolean; data: RestrictedDevice } }> =>
    api.post('/restricted-devices', device),

  update: (id: number, device: {
    deviceName?: string | null;
    allowedCampaignIds?: string[];
    allowedRoleIds?: string[];
    isActive?: boolean;
    sharedWorkstation?: boolean;
    machineDeviceId?: string;
  }): Promise<{ data: { success: boolean; data: RestrictedDevice } }> =>
    api.put(`/restricted-devices/${id}`, device),
  
  delete: (id: number): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/restricted-devices/${id}`),
};

// Devices API (Phase II - Sprint 2)
export const devicesAPI = {
  getMyDevice: (): Promise<{ data: { success: boolean; data: any[] } }> =>
    api.get('/devices/my-device'),
  
  getAll: (params?: { isTracked?: boolean; isActive?: boolean; userId?: string; search?: string }): Promise<{ data: { success: boolean; data: any[] } }> => {
    const queryParams = new URLSearchParams();
    if (params?.isTracked !== undefined) queryParams.append('isTracked', params.isTracked.toString());
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return api.get(`/devices${query ? `?${query}` : ''}`);
  },
  
  get: (id: string): Promise<{ data: { success: boolean; data: any } }> =>
    api.get(`/devices/${id}`),
  
  create: (device: { deviceId: string; deviceName?: string; userId?: string; notes?: string; agentVersion?: string }): Promise<{ data: { success: boolean; message: string; data: any } }> =>
    api.post('/devices', device),
  
  update: (id: string, device: { deviceName?: string; userId?: string | null; notes?: string; agentVersion?: string; isActive?: boolean }): Promise<{ data: { success: boolean; message: string; data: any } }> =>
    api.put(`/devices/${id}`, device),
  
  updateTracking: (id: string, isTracked: boolean, notes?: string): Promise<{ data: { success: boolean; message: string; data: any } }> =>
    api.put(`/devices/${id}/tracking`, { isTracked, notes }),
  
  delete: (id: string, options?: { permanent?: boolean }): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/devices/${id}`, { params: options?.permanent ? { permanent: true } : undefined }),
};

// Global Settings API
export const globalSettingsAPI = {
  getAll: (): Promise<{ data: { success: boolean; data: Record<string, { value: any; description: string; updatedAt: string }> } }> =>
    api.get('/global-settings'),
  
  get: (key: string): Promise<{ data: { success: boolean; data: { value: boolean; description: string } } }> => {
    // All settings now use the public /:key endpoint
    return api.get(`/global-settings/${key}`);
  },
  
  update: (key: string, value: any): Promise<{ data: { success: boolean; message: string; data: { key: string; value: any; updatedAt: string } } }> =>
    api.put(`/global-settings/${key}`, { value }),
  
  getTimeOffApprovers: (): Promise<{ data: { success: boolean; data: any } }> =>
    api.get('/global-settings/time-off-approvers'),
  
  setTimeOffApprovers: (data: { operationsApproverUserId: string; administrationApproverUserId: string; accountingApproverUserId?: string; ceoApproverUserId?: string }): Promise<{ data: { success: boolean; message: string } }> =>
    api.post('/global-settings/time-off-approvers', data),
};

// HR System API (external)
// Create a separate axios instance for HR system API
const hrApi = axios.create({
  baseURL: envConfig.hrApiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log HR API configuration for debugging
console.log('🔗 HR System API Configuration:', {
  baseURL: envConfig.hrApiUrl,
  environment: envConfig.environment,
  hostname: window.location.hostname,
});

// HR System Vacation Balance API
// Now calls backend endpoint which proxies to HR API to avoid CORS issues
export const hrVacationAPI = {
  /**
   * Get vacation balance for current user
   * @param employeeId - Employee ID from TEKS TIME (e.g., "001234") - kept for compatibility but not used
   * @returns Vacation balance summary
   */
  getBalance: (employeeId?: string): Promise<{ data: VacationBalanceSummary }> => {
    // Call backend endpoint which will proxy to HR API
    // The backend uses the authenticated user's employee ID
    console.log('🌐 Calling backend vacation balance endpoint:', {
      timestamp: new Date().toISOString()
    });
    
    return api.get('/time-absence-requests/vacation-balance').then(response => {
      // Backend returns { success: true, data: {...} }
      const apiData = response.data?.data || response.data;
      console.log('📥 Vacation Balance Response:', {
        status: response.status,
        responseDataType: typeof response.data,
        apiData: apiData,
        availableDays: apiData?.availableDays,
        usedDays: apiData?.usedDays,
        accruedDays: apiData?.accruedDays,
        usedDaysCompleted: apiData?.usedDaysCompleted,
        usedDaysApproved: apiData?.usedDaysApproved,
        keys: apiData ? Object.keys(apiData) : 'no data',
        fullResponse: JSON.stringify(apiData, null, 2),
        timestamp: new Date().toISOString()
      });
      
      // Return in expected format
      if (apiData && typeof apiData === 'object' && 'availableDays' in apiData) {
        return { data: apiData };
      } else if (apiData && typeof apiData === 'object' && 'data' in apiData) {
        console.log('⚠️ Response appears to be double-wrapped, unwrapping:', apiData);
        return { data: apiData.data };
      } else {
        console.warn('⚠️ Unexpected response structure:', apiData);
        return { data: apiData };
      }
    }).catch(error => {
      console.error('❌ Vacation Balance API Error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        fullUrl: error.config?.baseURL + error.config?.url
      });
      throw error;
    });
  },
};

// Alert Configuration API
export const alertConfigurationAPI = {
  getAll: (): Promise<{ data: { success: boolean; data: any[] } }> =>
    api.get('/alert-configurations'),
  
  getByRole: (role: string): Promise<{ data: { success: boolean; data: any } }> =>
    api.get(`/alert-configurations/${role}`),
  
  create: (config: any): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.post('/alert-configurations', config),
  
  update: (role: string, config: any): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put(`/alert-configurations/${role}`, config),
  
  delete: (role: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.delete(`/alert-configurations/${role}`),
  
  getRecipients: (role: string): Promise<{ data: { success: boolean; data: { escalationRecipients: string[] } } }> =>
    api.get(`/alert-configurations/${role}/recipients`),
  
  updateRecipients: (role: string, recipients: string[]): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put(`/alert-configurations/${role}/recipients`, { escalationRecipients: recipients }),
};

// User Notification Preferences API
export const userNotificationPreferencesAPI = {
  get: (): Promise<{ data: { success: boolean; data: any } }> =>
    api.get('/user-notification-preferences'),
  
  update: (preferences: any): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put('/user-notification-preferences', preferences),
  
  getByUserId: (userId: string): Promise<{ data: { success: boolean; data: any } }> =>
    api.get(`/user-notification-preferences/${userId}`),
  
  updateByUserId: (userId: string, preferences: any): Promise<{ data: { success: boolean; data: any; message: string } }> =>
    api.put(`/user-notification-preferences/${userId}`, preferences),
};

// Alert System API
export const alertSystemAPI = {
  getStatus: (): Promise<{ data: { success: boolean; data: { isEnabled: boolean; serviceRunning: boolean; lastChecked: string } } }> =>
    api.get('/alert-system/status'),
  
  start: (): Promise<{ data: { success: boolean; message: string } }> =>
    api.post('/alert-system/start'),
  
  stop: (): Promise<{ data: { success: boolean; message: string } }> =>
    api.post('/alert-system/stop'),
  
  getLogs: (params?: any): Promise<{ data: { success: boolean; data: { logs: any[]; total: number; page: number; limit: number; totalPages: number } } }> =>
    api.get('/alert-system/logs', { params }),
  
  getLogById: (id: string): Promise<{ data: { success: boolean; data: any } }> =>
    api.get(`/alert-system/logs/${id}`),
  
  getEmailContent: (id: string): Promise<{ data: { success: boolean; data: { subject: string; html: string; text: string; sentTo: string[]; sentAt: string; status: string } } }> =>
    api.get(`/alert-system/logs/${id}/email`),
  
  getStatistics: (params?: any): Promise<{ data: { success: boolean; data: any } }> =>
    api.get('/alert-system/statistics', { params }),
  
  sendTest: (communicationType: string): Promise<{ data: { success: boolean; message: string } }> =>
    api.post('/alert-system/test', { communicationType }),
  
  clearLogs: (): Promise<{ data: { success: boolean; message: string; data: { archivedCount: number } } }> =>
    api.delete('/alert-system/logs'),
};

// Attendance Report API
export const attendanceReportAPI = {
  getReport: (params: { startDate: string; endDate: string; employeeId?: string; campaignId?: string; format?: string }): Promise<{ data: any }> => {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    if (params.employeeId) queryParams.append('employeeId', params.employeeId);
    if (params.campaignId) queryParams.append('campaignId', params.campaignId);
    if (params.format) queryParams.append('format', params.format);
    
    return api.get(`/attendance-report?${queryParams.toString()}`);
  },
};

// Report Shares API
const reportSharesAPI = {
  share: async (data: {
    reportType: string;
    reportFilters: any;
    recipients: string;
    subject: string;
    message?: string;
    attachmentType: 'pdf' | 'excel' | 'csv' | 'link';
    reportData?: string | Blob;
    reportTitle?: string;
  }) => {
    // Send as JSON since we're not actually sending file data yet
    // When file generation is implemented, we can switch to FormData
    const response = await api.post('/report-shares', {
      reportType: data.reportType,
      reportFilters: data.reportFilters,
      recipients: data.recipients,
      subject: data.subject,
      message: data.message,
      attachmentType: data.attachmentType,
      reportData: data.reportData ? (data.reportData instanceof Blob ? await data.reportData.text() : data.reportData) : undefined,
      reportTitle: data.reportTitle
    });
    return response.data;
  },
  getHistory: async (params?: { page?: number; limit?: number; reportType?: string }) => {
    const response = await api.get('/report-shares', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get(`/report-shares/${id}`);
    return response.data;
  },
};

const operationsAPI = {
  getAttendanceCoverage: (params: {
    date?: string;
    campaignId?: string;
    supervisorId?: string;
    attendanceStatus?: string;
    coverageStatus?: string;
    coverageScope?: 'assigned' | 'all_coverage';
  }) => api.get('/operations/attendance-coverage', { params }),

  getCoverageAgentOptions: (params: { date: string; campaignId: string; absentEmployeeId: string }) =>
    api.get('/operations/coverage-agent-options', { params }),

  getCoverageHistory: (id: string) => api.get(`/operations/coverage-assignments/${id}/history`),

  getAgentTimeline: (employeeId: string, params: { date?: string }) =>
    api.get(`/operations/agents/${employeeId}/timeline`, { params }),

  getCampaignAssignmentSummary: (params: { date?: string; campaignId?: string }) =>
    api.get('/operations/campaign-assignment-summary', { params }),

  createCoverageAssignment: (data: any) => api.post('/operations/coverage-assignments', data),
  updateCoverageAssignment: (id: string, data: any) => api.put(`/operations/coverage-assignments/${id}`, data),
  deleteCoverageAssignment: (id: string, data: any) => api.delete(`/operations/coverage-assignments/${id}`, { data }),
  markCoverageNotRequired: (id: string, data?: { notes?: string; workDate?: string; absentEmployeeId?: string; absenceReason?: string }) =>
    api.post(`/operations/coverage-assignments/${id}/mark-not-required`, data || {}),
  previewSummary: (data: any) => api.post('/operations/attendance-coverage/preview-summary', data),
  sendSummary: (data: any) => api.post('/operations/attendance-coverage/send-summary', data),
  previewAlert: (data: any) => api.post('/operations/attendance-coverage/preview-alert', data),
  sendManagerAlert: (data: any) => api.post('/operations/attendance-coverage/send-manager-alert', data),
  getReportSchedules: () => api.get('/operations/report-schedules'),
  createReportSchedule: (data: any) => api.post('/operations/report-schedules', data),
  updateReportSchedule: (id: string, data: any) => api.put(`/operations/report-schedules/${id}`, data),
  deleteReportSchedule: (id: string) => api.delete(`/operations/report-schedules/${id}`),
  testReportSchedule: (id: string, data?: { date?: string }) => api.post(`/operations/report-schedules/${id}/test`, data || {}),
  createDailyNote: (data: { workDate: string; campaignId?: string; note: string }) => api.post('/operations/daily-notes', data),
  deleteDailyNote: (id: string) => api.delete(`/operations/daily-notes/${id}`),
  exportExcel: (params: Record<string, string | undefined>) =>
    api.get('/operations/attendance-coverage/export/excel', { params, responseType: 'blob' }),
  exportPdf: (params: Record<string, string | undefined>) =>
    api.get('/operations/attendance-coverage/export/pdf', { params, responseType: 'blob' }),
};

const notificationSettingsAPI = {
  get: () => api.get('/notification-settings'),
  getStatus: () => api.get('/notification-settings/status'),
  getRecipientOptions: () => api.get('/notification-settings/recipient-options'),
  update: (data: any) => api.put('/notification-settings', data),
  testEmail: (recipient: string) => api.post('/notification-settings/test-email', { recipient }),
  testSms: (recipient: string) => api.post('/notification-settings/test-sms', { recipient }),
  testWhatsApp: (recipient: string) => api.post('/notification-settings/test-whatsapp', { recipient }),
};

const manualTimeAdjustmentsAPI = {
  list: (params: Record<string, string | number | undefined>) =>
    api.get('/manual-time-adjustments', { params }),
  getById: (id: string) => api.get(`/manual-time-adjustments/${id}`),
  getForEmployee: (employeeId: string, params?: Record<string, string | undefined>) =>
    api.get(`/manual-time-adjustments/employee/${employeeId}`, { params }),
  review: (id: string, data: { reviewStatus: string; reviewComment?: string }) =>
    api.post(`/manual-time-adjustments/${id}/review`, data),
  exportCsv: (params: Record<string, string | number | undefined>) =>
    api.get('/manual-time-adjustments/export/csv', { params, responseType: 'blob' }),
};

const payrollAPI = {
  review: (params: Record<string, string | undefined>) =>
    api.get('/payroll/review', { params }),
  timecard: (employeeId: string, params: Record<string, string | undefined>) =>
    api.get(`/payroll/timecard/${employeeId}`, { params }),
  approveSupervisor: (employeeId: string, data: Record<string, string>) =>
    api.post(`/payroll/approve/${employeeId}`, data),
  approvePayroll: (employeeId: string, data: Record<string, string>) =>
    api.post(`/payroll/payroll-approve/${employeeId}`, data),
  lock: (employeeId: string, data: Record<string, string>) =>
    api.post(`/payroll/lock/${employeeId}`, data),
  unlock: (employeeId: string, data: Record<string, string>) =>
    api.post(`/payroll/unlock/${employeeId}`, data),
  closePeriod: (data: Record<string, string>) =>
    api.post('/payroll/period/close', data),
  reopenPeriod: (data: Record<string, string>) =>
    api.post('/payroll/period/reopen', data),
  reopen: (employeeId: string, data: Record<string, string>) =>
    api.post(`/payroll/reopen/${employeeId}`, data),
  exportCsv: (params: Record<string, string | undefined>) =>
    api.get('/payroll/export', { params: { ...params, format: 'csv' }, responseType: 'blob' }),
  policy: () => api.get('/payroll/policy'),
  periods: (params?: Record<string, string | undefined>) => api.get('/payroll/periods', { params }),
  bulk: (action: 'supervisor' | 'payroll' | 'lock', data: Record<string, string>) =>
    api.post(`/payroll/bulk/${action}`, data),
  savePayPeriod: (data: { type: string; biweeklyAnchorDate?: string | null; timezone?: string }) =>
    api.put('/payroll/policy/pay-period', data),
};

export const apiClient = {
  auth: authAPI,
  users: usersAPI,
  departments: departmentsAPI,
  campaigns: campaignsAPI,
  schedules: schedulesAPI,
  timeEntries: timeEntriesAPI,
  payroll: payrollAPI,
  absences: absenceAPI,
  dashboard: dashboardAPI,
  reports: reportsAPI,
  activity: activityAPI,
  roles: rolesAPI,
  password: passwordAPI,
  networkConfig: networkConfigAPI,
  globalSettings: globalSettingsAPI,
  alertConfiguration: alertConfigurationAPI,
  userNotificationPreferences: userNotificationPreferencesAPI,
  alertSystem: alertSystemAPI,
  attendanceReport: attendanceReportAPI,
  reportShares: reportSharesAPI,
  operations: operationsAPI,
  manualTimeAdjustments: manualTimeAdjustmentsAPI,
  notificationSettings: notificationSettingsAPI,
  devices: devicesAPI,
  activityTracking: {
    getMyActivity: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/activity-tracking/my-activity'),
    
    getIdleSummary: (params?: { startDate: string; endDate: string; userId?: string; deviceId?: string }): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/activity-tracking/idle-summary', { params }),
    
    getDeviceUptime: (params?: { startDate: string; endDate: string; deviceId?: string }): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/activity-tracking/device-uptime', { params }),
    
          getCurrentlyIdle: (): Promise<{ data: { success: boolean; data: any[] } }> =>
            api.get('/activity-tracking/currently-idle'),
          
          getCurrentlyOffline: (): Promise<{ data: { success: boolean; data: any[] } }> =>
            api.get('/activity-tracking/currently-offline'),
        },
  tracking: {
    getHealth: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/tracking/health'),
    
    getMetrics: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/tracking/metrics'),
  },
  monitoring: {
    getOverview: (params?: { date?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/overview', { params }),
    getLiveWorkforce: (params?: { date?: string }): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/workforce/live', { params }),
    getWorkforce: (params?: { date?: string; live?: boolean }): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/workforce', { params }),
    getActivity: (params?: Record<string, string | number | boolean | undefined>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/activity', { params }),
    getActivitySnapshot: (snapshotId: string): Promise<{ data: Blob; headers: Record<string, string> }> =>
      api.get(`/monitoring/activity/snapshots/${snapshotId}`, { responseType: 'blob' }),
    getDailyActivityReport: (params?: {
      date?: string;
      group_by?: 'employee' | 'computer';
      employee_id?: string;
      device_id?: string;
      campaign_id?: string;
      mode?: 'today_so_far' | 'final';
      timezone?: string;
    }): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/reports/daily-activity', { params }),
    getDailyActivityEmailStatus: (params: { date: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/reports/daily-activity/email-status', { params }),
    previewDailyActivityEmail: (params: {
      date: string;
      mode?: 'today_so_far' | 'final';
    }): Promise<{ data: { success: boolean; data: { subject: string; html: string; package: any } } }> =>
      api.get('/monitoring/reports/daily-activity/email-preview', { params }),
    sendDailyActivityEmail: (data: {
      date: string;
      mode?: 'today_so_far' | 'final';
      force_resend?: boolean;
    }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/monitoring/reports/daily-activity/email-send', data),
    exportDailyActivityReport: (params?: {
      date?: string;
      group_by?: 'employee' | 'computer';
      employee_id?: string;
      device_id?: string;
      campaign_id?: string;
    }): Promise<{ data: Blob }> =>
      api.get('/monitoring/reports/daily-activity/export', { params, responseType: 'blob' }),
    getApplications: (params?: Record<string, string | undefined>): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/applications', { params }),
    getWebActivity: (params?: Record<string, string | undefined>): Promise<{
      data: {
        success: boolean;
        data: any[];
        meta?: {
          events_considered?: number;
          quarantined_invalid_timestamps?: number;
          duration_basis?: string;
          foreground_verified?: boolean;
        };
      };
    }> =>
      api.get('/monitoring/web-activity', { params }),
    getSessions: (params?: Record<string, string | undefined>): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/sessions', { params }),
    getAlerts: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/alerts'),
    listWorkforceAlerts: (params?: { status?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/alerts/workforce', { params }),
    evaluateWorkforceAlerts: (data?: { work_date?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/monitoring/alerts/evaluate', data || {}),
    getCampaignMismatches: (params?: { date?: string }): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/campaign-mismatches', { params }),
    getPayrollReconciliation: (params?: { date?: string; employee_id?: string }): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/reports/payroll-reconciliation', { params }),
    getConfiguration: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/configuration'),
    updateConfiguration: (data: Record<string, string | number | boolean>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/monitoring/configuration', data),
    getMonitoringPolicy: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/monitoring-policy'),
    updateMonitoringPolicy: (data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/monitoring/monitoring-policy', data),
    getMonitoringAcknowledgments: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/acknowledgments'),
    getProductivityConfig: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/productivity/config'),
    updateProductivityConfig: (data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/monitoring/productivity/config', data),
    listProductivityClassifications: (params?: Record<string, string | undefined>): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/productivity/classifications', { params }),
    upsertProductivityClassification: (data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/monitoring/productivity/classifications', data),
    deleteProductivityClassification: (id: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.delete(`/monitoring/productivity/classifications/${id}`),
    getUnclassifiedProductivityCandidates: (params?: Record<string, string | number | undefined>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/productivity/unclassified', { params }),
    getTimeline: (employeeId: string, params?: { date?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/timeline/${employeeId}`, { params }),
    getPayrollComparison: (employeeId: string, params?: { date?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/payroll-comparison/${employeeId}`, { params }),
    generateEmergencyBypass: (data: {
      employee_id?: string;
      device_id?: string;
      campaign_id?: string;
      reason?: string;
      reason_code?: string;
      reason_text?: string;
      duration_minutes?: number;
      redeem_expires_minutes?: number;
    }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/monitoring/emergency-bypass/generate', data),
    generateEmergencyCode: (data: {
      employee_id?: string;
      device_id?: string;
      campaign_id?: string;
      reason?: string;
      reason_code?: string;
      reason_text?: string;
      duration_minutes?: number;
      redeem_expires_minutes?: number;
    }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/monitoring/emergency-access/codes', data),
    listEmergencyBypass: (): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/emergency-bypass'),
    getActiveEmergencyAccess: (): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/monitoring/emergency-access/active'),
    revokeEmergencyCode: (codeId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/monitoring/emergency-access/${codeId}/revoke`),
    endEmergencyBypass: (codeId: string, data?: { end_reason?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/monitoring/emergency-access/${codeId}/end`, data),
    getRequiredAction: (params: { employee_id?: string; device_id?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/required-action', { params }),
    getDevices: (params?: Record<string, string | undefined>): Promise<{ data: { success: boolean; data: any[]; counts?: Record<string, number> } }> =>
      api.get('/monitoring/devices', { params }),
    listDevices: (params?: Record<string, string | undefined>): Promise<{ data: { success: boolean; data: any[]; counts?: Record<string, number> } }> =>
      api.get('/monitoring/devices', { params }),
    getDeviceDiagnostics: (deviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/devices/${deviceId}/diagnostics`),
    runDeviceDiagnostics: (deviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/monitoring/devices/${deviceId}/diagnostics/run`),
    runActivityPipelineSelfTest: (deviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/monitoring/devices/${deviceId}/diagnostics/activity-pipeline-self-test`),
    verifyPilotTest: (deviceId: string, data: { test_type: string; notes?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/monitoring/devices/${deviceId}/pilot-validation`, data),
    pullDeviceAgentLogs: (
      deviceId: string,
      data?: { max_bytes?: number; max_lines?: number },
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/monitoring/devices/${deviceId}/agent-logs/pull`, data || {}),
    listDeviceAgentLogs: (
      deviceId: string,
      params?: { limit?: number },
    ): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get(`/monitoring/devices/${deviceId}/agent-logs`, { params }),
    getDeviceAgentLogSnapshot: (
      deviceId: string,
      snapshotId: string,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/devices/${deviceId}/agent-logs/${snapshotId}`),
    downloadDeviceAgentLogSnapshot: async (
      deviceId: string,
      snapshotId: string,
      fileName?: string,
    ): Promise<void> => {
      const response = await api.get(
        `/monitoring/devices/${deviceId}/agent-logs/${snapshotId}`,
        { params: { download: '1' }, responseType: 'blob' },
      );
      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName || `agent-log-${snapshotId}.txt`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    },
    getLiveWall: (params?: Record<string, string | boolean | undefined>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/live-wall', { params }),
    getLiveScreenConfig: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/live-screen/config'),
    updateLiveScreenConfig: (data: Record<string, any>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/monitoring/live-screen/config', data),
    getLiveScreenCapabilities: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/live-screen/capabilities'),
    getLiveScreenDiagnostics: (deviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/live-screen/${deviceId}/diagnostics`),
    auditLiveScreenView: (
      deviceId: string,
      body: {
        event: 'start' | 'end';
        mode?: string;
        correlation_id?: string;
        duration_seconds?: number;
      },
    ): Promise<{ data: { success: boolean } }> =>
      api.post(`/monitoring/live-screen/${deviceId}/view-audit`, body),
    updateDeviceLiveScreen: (
      deviceId: string,
      data: { enabled: boolean; reason?: string },
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.put(`/monitoring/live-screen/devices/${deviceId}`, data),
    getRemoteControlConfig: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/remote-control/config'),
    updateRemoteControlConfig: (data: Record<string, any>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/monitoring/remote-control/config', data),
    updateDeviceRemoteControl: (
      deviceId: string,
      data: { enabled: boolean; reason?: string },
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.put(`/monitoring/remote-control/devices/${deviceId}`, data),
    getCallMonitoringConfig: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/call-monitoring/config'),
    updateCallMonitoringConfig: (data: Record<string, any>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/monitoring/call-monitoring/config', data),
    getDeviceCallMonitoring: (deviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/call-monitoring/devices/${deviceId}`),
    updateDeviceCallMonitoring: (
      deviceId: string,
      data: { enabled: boolean; reason?: string },
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.put(`/monitoring/call-monitoring/devices/${deviceId}`, data),
    requestAgentUninstall: (
      deviceId: string,
      data: { confirm_device_name: string },
    ): Promise<{ data: { success: boolean; data: any; message?: string } }> =>
      api.post(`/monitoring/devices/${deviceId}/uninstall-agent`, data),
    cancelAgentUninstall: (
      deviceId: string,
    ): Promise<{ data: { success: boolean; data: any; message?: string } }> =>
      api.delete(`/monitoring/devices/${deviceId}/uninstall-agent`),
    getRemoteControlDiagnostics: (deviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/remote-control/${deviceId}/diagnostics`),
    getRemoteControlStatus: (deviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/remote-control/${deviceId}/status`),
    listRemoteControlSessions: (
      params?: Record<string, string | number | undefined>,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/remote-control/sessions', { params }),
    getRemoteControlSession: (
      sessionId: string,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/remote-control/sessions/${encodeURIComponent(sessionId)}`),
    listDeviceRemoteControlSessions: (
      deviceId: string,
      params?: Record<string, string | number | undefined>,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/remote-control/devices/${encodeURIComponent(deviceId)}/sessions`, { params }),
    listRemoteSupportActions: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/remote-control/actions'),
    uploadRemoteControlFile: (
      sessionId: string,
      controlToken: string,
      file: File,
      options?: {
        onProgress?: (percent: number) => void;
        signal?: AbortSignal;
      },
    ): Promise<{
      transferId: string;
      fileName: string;
      byteSize?: number;
      sha256?: string;
      status?: string;
    }> => new Promise((resolve, reject) => {
      const token = localStorage.getItem('token');
      if (!token) {
        reject(new Error('Authentication required'));
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      const xhr = new XMLHttpRequest();
      const base = API_BASE_URL.replace(/\/$/, '');
      xhr.open('POST', `${base}/monitoring/remote-control/sessions/${encodeURIComponent(sessionId)}/files/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('x-control-token', controlToken);
      const deviceId = generateDeviceId();
      if (deviceId) {
        xhr.setRequestHeader('X-Device-ID', deviceId);
      }
      if (options?.onProgress && xhr.upload) {
        xhr.upload.onprogress = (evt) => {
          if (!evt.lengthComputable || evt.total <= 0) return;
          options.onProgress!(Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
        };
      }
      const onAbort = () => {
        xhr.abort();
      };
      if (options?.signal) {
        if (options.signal.aborted) {
          reject(new Error('Upload cancelled'));
          return;
        }
        options.signal.addEventListener('abort', onAbort, { once: true });
      }
      xhr.onload = () => {
        options?.signal?.removeEventListener('abort', onAbort);
        let body: any = null;
        try {
          body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          body = null;
        }
        if (xhr.status >= 200 && xhr.status < 300 && body?.success !== false) {
          const data = body?.data || body || {};
          resolve({
            transferId: String(data.transferId || data.id || ''),
            fileName: String(data.fileName || file.name),
            byteSize: data.byteSize != null ? Number(data.byteSize) : file.size,
            sha256: data.sha256,
            status: data.status,
          });
          return;
        }
        reject(new Error(body?.message || body?.error || `Upload failed (${xhr.status})`));
      };
      xhr.onerror = () => {
        options?.signal?.removeEventListener('abort', onAbort);
        reject(new Error('Upload network error'));
      };
      xhr.onabort = () => {
        options?.signal?.removeEventListener('abort', onAbort);
        reject(new Error('Upload cancelled'));
      };
      xhr.send(formData);
    }),
    cancelRemoteControlFileTransfer: (
      transferId: string,
    ): Promise<{ data: { success: boolean; data?: any } }> =>
      api.post(`/monitoring/remote-control/transfers/${encodeURIComponent(transferId)}/cancel`),
    getRemoteControlTransferContentUrl: (transferId: string): string => {
      const base = API_BASE_URL.replace(/\/$/, '');
      return `${base}/monitoring/remote-control/transfers/${encodeURIComponent(transferId)}/content`;
    },
    downloadRemoteControlTransferContent: async (
      transferId: string,
      fileName?: string,
    ): Promise<void> => {
      const response = await api.get(
        `/monitoring/remote-control/transfers/${encodeURIComponent(transferId)}/content`,
        { responseType: 'blob' },
      );
      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName || `remote-file-${transferId}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    },
    listEnforcementPolicies: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/monitoring/enforcement-policies'),
    getEnforcementPolicy: (id: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/enforcement-policies/${id}`),
    updateEnforcementPolicy: (
      id: string,
      data: Record<string, unknown>,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.put(`/monitoring/enforcement-policies/${id}`, data),
    assignEnforcementPolicyToRole: (
      roleName: string,
      policyId: string,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.put(`/monitoring/enforcement-policies/roles/${encodeURIComponent(roleName)}`, {
        policy_id: policyId,
      }),
    assignEmployeeEnforcementOverride: (
      employeeId: string,
      policyId: string,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.put(`/monitoring/enforcement-policies/employees/${encodeURIComponent(employeeId)}`, {
        policy_id: policyId,
      }),
    removeEmployeeEnforcementOverride: (
      employeeId: string,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.delete(`/monitoring/enforcement-policies/employees/${encodeURIComponent(employeeId)}`),
    getEffectiveEnforcementPolicy: (
      employeeId: string,
    ): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/monitoring/enforcement-policies/effective/${encodeURIComponent(employeeId)}`),
  },
  callQa: {
    getOverview: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/overview'),
    getProviderHealth: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/provider-health'),
    getConfiguration: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/configuration'),
    getAlertEmailSettings: (): Promise<{ data: { success: boolean; data: { levels: string[]; recipients: string[]; availableLevels: string[] } } }> =>
      api.get('/call-qa/alert-email-settings'),
    updateAlertEmailSettings: (data: { levels?: string[]; recipients?: string[] }) =>
      api.put('/call-qa/alert-email-settings', data),
    getConductSettings: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/conduct/settings'),
    updateConductSettings: (data: {
      master_enabled?: boolean;
      warning_enabled?: boolean;
      serious_enabled?: boolean;
      critical_enabled?: boolean;
      legacy_alert_emails_enabled?: boolean;
      recipients?: string[];
    }): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/call-qa/conduct/settings', data),
    listConductIncidents: (params?: Record<string, string | number | undefined>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/conduct/incidents', { params }),
    getCallConduct: (callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/calls/${callId}/conduct`),
    updateConductIncident: (incidentId: string, data: { review_status: string; review_notes?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.patch(`/call-qa/conduct/incidents/${incidentId}`, data),
    updateConfiguration: (data: {
      enabled?: boolean;
      retention_days?: number;
      auto_transcribe?: boolean;
      preferred_language?: string;
      transcription_source_preference?: string;
      search_preroll_ms?: number;
      auto_score?: boolean;
    }): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/call-qa/configuration', data),
    upsertProvider: (data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/call-qa/providers', data),
    listCalls: (params?: Record<string, string | number | undefined>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/calls', { params }),
    searchTranscripts: (params?: Record<string, string | number | undefined>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/transcripts/search', { params }),
    getCall: (callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/calls/${callId}`),
    getTranscript: (callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/calls/${callId}/transcript`),
    searchWithinCall: (callId: string, params?: Record<string, string | number | undefined>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/calls/${callId}/transcript/search`, { params }),
    requestTranscription: (callId: string, data?: { force?: boolean; retry?: boolean }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/transcription/request`, data || {}),
    correctSpeakerRole: (segmentId: string, data: { speaker_role: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.patch(`/call-qa/transcript-segments/${segmentId}/speaker-role`, data),
    importRecording: (formData: FormData): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/call-qa/calls/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    updateCall: (callId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.patch(`/call-qa/calls/${callId}`, data),
    linkEmployee: (callId: string, data: { employee_id: string | null }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/link-employee`, data),
    linkCampaign: (callId: string, data: { campaign_id: string | null }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/link-campaign`, data),
    streamRecording: (callId: string): Promise<{ data: Blob }> =>
      api.get(`/call-qa/calls/${callId}/recording/stream`, { responseType: 'blob' }),
    streamRecordingSource: (callId: string, recordingId: string): Promise<{ data: Blob }> =>
      api.get(`/call-qa/calls/${callId}/recordings/${recordingId}/stream`, { responseType: 'blob' }),
    downloadRecording: (callId: string): Promise<{ data: Blob; headers?: Record<string, string> }> =>
      api.get(`/call-qa/calls/${callId}/recording/download`, { responseType: 'blob' }),
    downloadCombinedRecording: (callId: string): Promise<{ data: Blob; headers?: Record<string, string> }> =>
      api.get(`/call-qa/calls/${callId}/recording/combined`, { responseType: 'blob' }),
    getGlobalScorecard: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/scorecards/global'),
    listScorecardVersions: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/scorecards/versions'),
    updateGlobalScorecard: (data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/call-qa/scorecards/global', data),
    listServices: (includeDisabled?: boolean): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/services', { params: includeDisabled ? { includeDisabled: true } : undefined }),
    createService: (data: { name: string; code?: string; sort_order?: number }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/call-qa/services', data),
    updateService: (serviceId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.patch(`/call-qa/services/${serviceId}`, data),
    getServiceScorecard: (serviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/scorecards/service/${serviceId}`),
    createServiceScorecard: (serviceId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/scorecards/service/${serviceId}`, {}),
    updateServiceScorecard: (serviceId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put(`/call-qa/scorecards/service/${serviceId}`, data),
    deleteScorecardVersion: (scorecardId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.delete(`/call-qa/scorecards/${scorecardId}`),
    getCallScore: (callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/calls/${callId}/score`),
    requestScoring: (callId: string, data?: { force?: boolean; mode?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/scoring/request`, data || {}),
    reanalyzeCall: (callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/reanalyze`, {}),
    acceptScore: (callId: string, data?: { review_notes?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/scoring/accept`, data || {}),
    overrideScore: (callId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/scoring/override`, data),
    listAgents: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/agents'),
    getAgent: (employeeId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/agents/${employeeId}`),
    listCampaignConfigs: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/campaigns'),
    getCampaignConfig: (campaignId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/campaigns/${campaignId}`),
    updateCampaignConfig: (campaignId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.put(`/call-qa/campaigns/${campaignId}`, data),
    getCallFlags: (callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/calls/${callId}/flags`),
    getActivityNotes: (callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/calls/${callId}/activity-notes`),
    createActivityNote: (callId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/activity-notes`, data),
    reviewActivityNote: (noteId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.patch(`/call-qa/activity-notes/${noteId}`, data),
    rescanCallFlags: (callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/calls/${callId}/flags/rescan`),
    listInvestigations: (params?: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/investigations', { params }),
    createInvestigation: (data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/call-qa/investigations', data),
    getInvestigation: (investigationId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/investigations/${investigationId}`),
    updateInvestigation: (investigationId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.patch(`/call-qa/investigations/${investigationId}`, data),
    linkInvestigationCall: (investigationId: string, data: { call_id: string; notes?: string }): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/investigations/${investigationId}/calls`, data),
    unlinkInvestigationCall: (investigationId: string, callId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.delete(`/call-qa/investigations/${investigationId}/calls/${callId}`),
    addInvestigationEvidence: (investigationId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.post(`/call-qa/investigations/${investigationId}/evidence`, data),
    listCoaching: (params?: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/coaching', { params }),
    createCoaching: (data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/call-qa/coaching', data),
    getCoaching: (coachingId: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/coaching/${coachingId}`),
    updateCoaching: (coachingId: string, data: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.patch(`/call-qa/coaching/${coachingId}`, data),
    getReport: (reportType: string, params?: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/call-qa/reports/${reportType}`, { params }),
    getReviewQueue: (params?: Record<string, unknown>): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/call-qa/review-queue', { params }),
  },
  enforcement: {
    getMasterPassword: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/enforcement/master-password'),
    setMasterPassword: (password: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/enforcement/master-password', { password }),
    rotateMasterPassword: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.post('/enforcement/master-password/rotate', {}),
    setFailOpenSeconds: (seconds: number): Promise<{ data: { success: boolean; data: any } }> =>
      api.put('/enforcement/fail-open', { seconds }),
  },
  installer: {
    getLatest: (): Promise<{ data: { success: boolean; data: any } }> =>
      api.get('/installer/latest'),
    
    getVersions: (): Promise<{ data: { success: boolean; data: any[] } }> =>
      api.get('/installer/versions'),
    
    verifyAdminPassword: (email: string, password: string): Promise<{ data: { success: boolean; message: string } }> =>
      api.post('/installer/verify-admin-password', { email, password }),
  },
  ipApproval: {
    getRequest: (token: string): Promise<{ data: { success: boolean; data: any } }> =>
      api.get(`/ip-approval/request/${token}`),
    
    approve: (token: string, finalCidr: string): Promise<{ data: { success: boolean; message: string; data: any } }> =>
      api.post(`/ip-approval/approve/${token}`, { finalCidr }),
    
    reject: (token: string, reason: string): Promise<{ data: { success: boolean; message: string; data: any } }> =>
      api.post(`/ip-approval/reject/${token}`, { reason }),
  },
};

export default api;

/** TOGO OPS bonus tracking through the TEKS TIME pass-through (/api/bonus-portal). */

export const bonusTrackerAPI = {
  me: async () => (await api.get('/bonus-tracker/me')).data.data,
  catalog: async (all = false) => (await api.get('/bonus-tracker/catalog', { params: all ? { all: 1 } : {} })).data.data,
  createCategory: async (input: any) => (await api.post('/bonus-tracker/catalog', input)).data.data,
  updateCategory: async (id: string, input: any) => (await api.patch(`/bonus-tracker/catalog/${id}`, input)).data.data,
  campaigns: async () => (await api.get('/bonus-tracker/campaigns')).data.data,
  employees: async (search?: string, campaignId?: string, range?: { from?: string; to?: string }, everyone?: boolean) =>
    (await api.get('/bonus-tracker/employees', { params: { search, campaignId, from: range?.from, to: range?.to, everyone: everyone ? 1 : undefined } })).data.data,
  records: async (params: Record<string, string | undefined> = {}) => (await api.get('/bonus-tracker/records', { params })).data.data,
  record: async (id: string) => (await api.get(`/bonus-tracker/records/${id}`)).data.data,
  createRecord: async (input: any) => (await api.post('/bonus-tracker/records', input)).data.data,
  updateRecord: async (id: string, patch: any) => (await api.patch(`/bonus-tracker/records/${id}`, patch)).data.data,
  addLines: async (id: string, lines: any[]) => (await api.post(`/bonus-tracker/records/${id}/lines`, { lines })).data.data,
  updateLine: async (lineId: string, patch: { amount?: number; concept?: string }) => (await api.patch(`/bonus-tracker/lines/${lineId}`, patch)).data.data,
  removeLine: async (lineId: string, reason: string) => (await api.post(`/bonus-tracker/lines/${lineId}/remove`, { reason })).data.data,
  decide: async (id: string, input: { stage: string; decision: 'Y' | 'N'; date?: string; note?: string; customerAnswer?: 'Y' | 'N'; customerDate?: string }) =>
    (await api.post(`/bonus-tracker/records/${id}/decision`, input)).data.data,
  uploadAttachments: async (id: string, kind: 'PROOF' | 'SCREENSHOT', files: File[]) => {
    const form = new FormData();
    form.append('kind', kind);
    files.forEach((f) => form.append('files', f));
    return (await api.post(`/bonus-tracker/records/${id}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data.data;
  },
  deleteAttachment: async (id: string, attachmentId: string) => (await api.delete(`/bonus-tracker/records/${id}/attachments/${attachmentId}`)).data.data,
  attachmentBlob: async (id: string, attachmentId: string) =>
    (await api.get(`/bonus-tracker/records/${id}/attachments/${attachmentId}/file`, { responseType: 'blob' })).data as Blob,
  cancelDecision: async (id: string, input: { stage: string; note: string }) =>
    (await api.post(`/bonus-tracker/records/${id}/cancel-decision`, input)).data.data,
  deleteRecord: async (id: string) => (await api.delete(`/bonus-tracker/records/${id}`)).data.data,
  importRows: async (rows: any[]) => (await api.post('/bonus-tracker/records/import', { rows })).data.data as { records: number; employees: number; skipped: Array<{ row: number; reason: string }> },
  exportCsv: async (params: Record<string, string | undefined> = {}) =>
    (await api.get('/bonus-tracker/export', { params, responseType: 'blob' })).data as Blob,
};

/** TEKS Solutions bonus month from TOGO OPS: read only (approvals happen in TOGO OPS). */
export const bonusPortalAPI = {
  me: async () => (await api.get('/bonus-portal/me')).data,
  teksSolutionsPeriods: async () => (await api.get('/bonus-portal/teks-solutions/periods')).data,
  teksSolutionsGet: async (year: number, month: number, category?: string) =>
    (await api.get('/bonus-portal/teks-solutions', { params: { year, month, category } })).data,
};
